import { useService } from '@/composables/useServices';
import { Deferred } from '@/core/helpers/deferredPromise';
import { Contact } from '@/models/contact';
import { User } from '@/models/user';
import { UserService } from '@/services/auth/userService';
import { FirestoreService } from '@/services/data/firebase/db/firestoreService';
import { Unsubscribe } from '@/services/data/firebase/db/iFirestoreService';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const ContactsStore = defineStore('contacts', () => {
  const contacts = ref<Contact[]>([]);
  const users = ref<User[]>([]);

  const initialized = ref(false);
  const loggedInContact = ref<Contact>();
  const loggedInUser = ref<User>();

  const unsubscribe = ref<Unsubscribe>();
  const subscribers = ref<string[]>([]);

  const firestoreService = useService(FirestoreService);
  const userService = useService(UserService);

  const employees = computed(() =>
    contacts.value.filter((contact: Contact) => {
      if (contact.archived) return false;
      if (!contact.type.includes('Employee')) return false;

      const user = users.value.find((user) => user?.customClaims?.contactRecord === contact.id);
      return user?.customClaims?.role !== undefined;
    }),
  );

  const loggedInContactName = computed(() => {
    return loggedInContact.value?.fullName || loggedInContact.value?.name || '';
  });

  async function initialize(): Promise<void> {
    if (initialized.value) return;

    const contactsLoaded = Deferred.promise();

    unsubscribe.value = firestoreService.subscribeToCollection<Contact>('contacts', (results) => {
      contacts.value = results.sort((a, b) => a.fullName.localeCompare(b.fullName));
      contactsLoaded.resolve();
    });

    users.value = await userService.getAllUsers<User>();

    await contactsLoaded.promise;

    loggedInUser.value = users.value.find((user) => user.uid === userService.getCurrentUser().uid);
    loggedInContact.value = contacts.value.find((contact) => contact.id === loggedInUser.value?.customClaims?.contactRecord);

    initialized.value = true;
  }

  function dispose(): void {
    if (unsubscribe.value) {
      unsubscribe.value();
      unsubscribe.value = undefined;
    }
    contacts.value = [];
    users.value = [];
    loggedInContact.value = undefined;
  }

  return {
    initialize,
    contacts,
    employees,
    loggedInContact,
    loggedInContactName,
    users,
    subscribers,
    dispose,
  };
});
